import { useGetCategoryPathQuery } from '@kijiji/generated/graphql-types'
import { useSession } from 'next-auth/react'
import { type FC, type PropsWithChildren } from 'react'

import { createVipGptConfig } from '@/components-page/vip/advertisement/createVipGptConfig'
import { generateVipTargetingValues } from '@/components-page/vip/advertisement/generateVipGptTargetingValues'
import { AdsWrapper } from '@/features/advertisement/components/ads-wrapper/AdsWrapper'
import { prebidConfig } from '@/features/advertisement/components/prebid/prebidConfig'
import useCookiePreferences from '@/hooks/useCookiePreferences'
import { useLocale } from '@/hooks/useLocale'
import { type VipQueryListing } from '@/types/listings'

export type VipAdsWrapperProps = PropsWithChildren & {
  listing: VipQueryListing
  isModalVisible: boolean
}

export const VipAdsWrapper: FC<VipAdsWrapperProps> = ({ listing, children, isModalVisible }) => {
  const { hasUserOptedOutTargeting = false } = useCookiePreferences()
  const { routeLocale, cookieLocale } = useLocale()
  const { status: userStatus } = useSession()

  const { data } = useGetCategoryPathQuery({
    fetchPolicy: 'cache-first',
    variables: {
      categoryId: listing.categoryId,
      locale: cookieLocale,
      locationId: listing.location.id,
    },
  })

  const categoryPath = data?.category?.categoryPaths ?? []
  const isMlsAd = listing.__typename === 'RealEstateListing' ? !!listing.mlsAd : false

  const gptTargetingValues = generateVipTargetingValues({
    adType: listing.type,
    categoryPath,
    categoryId: listing.categoryId,
    hasUserOptedOutTargeting,
    isMlsAd,
    listingAttributes: listing.attributes?.all,
    listingFlags: listing.flags,
    listingId: listing.id,
    listingPrice: listing.price,
    locationId: listing.location.id,
    routeLocale,
    userStatus,
  })

  const vipGptConfig = data
    ? createVipGptConfig({
        categoryPath,
        gptTargetingValues,
        routeLocale,
        isModalVisible,
      })
    : undefined

  /**
   * It should wait for the categoryPath to be available before it can load the ads
   * */
  return (
    <AdsWrapper gptConfig={vipGptConfig} prebidConfig={prebidConfig} skipBlockthrough>
      {children}
    </AdsWrapper>
  )
}
